import type { CSSProperties, ReactElement } from 'react';
import { ImageSlot } from '../../../components/ImageSlot';
import { imagePatchFor, resolveNodeImageUrl } from '../content';
import { StudioBox, warnUnsupportedNode, type StudioNodeRendererProps } from './box';

/** `props.crop` ('cover' | 'contain') maps straight onto `object-fit` (spec: "honour props.aspect/crop via object-fit"). */
function objectFitStyle(crop: string | undefined): CSSProperties {
  return { objectFit: crop === 'contain' ? 'contain' : 'cover', width: '100%', height: '100%', display: 'block' };
}

/**
 * Image / HeroImage: the same replace-image affordance every other
 * momentum_v2 photo slot uses (spec §84 — image replacement keeps working),
 * sized to the resolver's box and cropped per `props.crop`.
 */
function ImageLikeNode({ node, ctx }: StudioNodeRendererProps): ReactElement {
  const { section, proposal, actions, readOnly } = ctx;
  const contentRef = node.props?.contentRef;
  const url = resolveNodeImageUrl(section, node.props);
  const defaultQuery = node.props?.imageIntent || section.title || 'campaign visual';
  return (
    <StudioBox node={node} className="tpl-studio-image" style={{ overflow: 'hidden' }}>
      {url ? <img src={url} alt="" aria-hidden="true" style={objectFitStyle(node.props?.crop)} /> : null}
      <ImageSlot
        url={url}
        alt={section.title}
        readOnly={readOnly}
        hideImage
        proposalId={proposal.proposalId}
        defaultQuery={defaultQuery}
        className="tpl-studio-image-affordance"
        onSave={(next) => actions.saveContent(section, imagePatchFor(section, contentRef, next))}
      />
    </StudioBox>
  );
}

export function ImageNode(props: StudioNodeRendererProps): ReactElement {
  return <ImageLikeNode {...props} />;
}

export function HeroImageNode(props: StudioNodeRendererProps): ReactElement {
  return <ImageLikeNode {...props} />;
}

/** Read-only lockup image — no rep-facing replace affordance (agency/advertiser branding, not campaign photography). */
export function LogoNode({ node, ctx }: StudioNodeRendererProps): ReactElement | null {
  const { section, proposal } = ctx;
  const url = resolveNodeImageUrl(section, node.props) || proposal.document?.theme.logoUrl || proposal.advertiser.logoUrl;
  if (!url) return null;
  return (
    <StudioBox node={node} className="tpl-studio-logo">
      <img src={url} alt="" style={{ objectFit: 'contain', width: '100%', height: '100%' }} />
    </StudioBox>
  );
}

export function ImageMaskNode(props: StudioNodeRendererProps): null {
  warnUnsupportedNode(props.node);
  return null;
}

export function DeviceMockupNode(props: StudioNodeRendererProps): null {
  warnUnsupportedNode(props.node);
  return null;
}

export function IconNode(props: StudioNodeRendererProps): null {
  warnUnsupportedNode(props.node);
  return null;
}
